import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Loader2, Mail } from 'lucide-react';
import { POST, handleApiError } from '@/shared/api/requests';
import AccountBase from '@/shared/components/accounts/AccountBase';

interface StatusState {
  type: 'success' | 'error' | '';
  message: string;
}

const ResendVerification: React.FC = () => {
  const [email, setEmail] = React.useState('');
  const [status, setStatus] = React.useState<StatusState>({ type: '', message: '' });
  const [isLoading, setIsLoading] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await POST('/users/auth/resend-verification/', { email });
      setStatus({
        type: 'success',
        message: 'Confirmation email sent. Please check your inbox.',
      });
      setEmail('');
    } catch (error) {
      setStatus({
        type: 'error',
        message: handleApiError(error),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AccountBase>
      <div className="container max-w-md mx-auto p-6">
        <Card className="shadow-md">
          <CardHeader>
            <div className="flex flex-col items-center space-y-4">
              <div className="rounded-full bg-primary/10 p-3">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-2xl text-center">Email Confirmation</CardTitle>
              <CardDescription className="text-center">
                Please check your email to confirm your email address.
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                If you have not received the confirmation email, check your spam folder or send a
                new one:
              </p>

              {status.type && (
                <Alert variant={status.type === 'error' ? 'destructive' : 'default'}>
                  <AlertDescription>{status.message}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Resend Confirmation Email'
                  )}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
      </div>
    </AccountBase>
  );
};

export default ResendVerification;
